/** The shared enquiry form: contact messages and buy-requests.
 *
 * Scheduling happens in a calendar (month → day → time, see Scheduler.tsx)
 * and follows the admin's `booking_mode` setting:
 *   request → the visitor picks preferred times, the owner confirms later
 *   live    → the visitor books a verified-free slot immediately
 *   both    → the live calendar, with a switch to propose different times
 * If the live calendar cannot be verified (CalDAV down), the form silently
 * falls back to the request flow - never a dead end.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, api, type Captcha, type SubmitResult } from '../lib/api'
import { useLocale, useSiteConfig } from '../lib/hooks'
import { fill, t } from '../lib/i18n'
import { VisitorScheduler } from './Scheduler'

interface Props {
  productSlug?: string
  defaultSubject?: string
  /** Contact page shows the subject field; product pages don't need it. */
  showSubject?: boolean
  /** "Worum geht es?" chips - contact page only. */
  showTopics?: boolean
  defaultTopic?: string
  /** Photo upload (damage reports etc.) - contact page only. */
  showPhotos?: boolean
}

const TOPIC_KEYS = ['repair', 'appraisal', 'purchase', 'other'] as const

interface UploadedPhoto {
  filename: string
  preview: string
}

const MAX_PHOTOS = 3

/** Fields: white on paper, hairline border, brass focus ring. Labels are
 *  visible above each field. */
const inputClass =
  'w-full rounded-[2px] border border-line bg-white px-4 py-[13px] font-sans text-[17px] ' +
  'text-ink placeholder:text-slate/70 outline-none transition-[border-color,box-shadow] ' +
  'focus:border-amber focus:ring-2 focus:ring-amber/30'

const labelClass = 'mb-1.5 block font-sans text-[14px] font-semibold text-slate'

export default function InquiryForm({
  productSlug,
  defaultSubject = '',
  showSubject = true,
  showTopics = false,
  defaultTopic = '',
  showPhotos = false,
}: Props) {
  const locale = useLocale()
  const strings = t(locale)
  const config = useSiteConfig()

  const [topic, setTopic] = useState(defaultTopic)
  const [photos, setPhotos] = useState<UploadedPhoto[]>([])
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const photoInput = useRef<HTMLInputElement>(null)

  const [captcha, setCaptcha] = useState<Captcha | null>(null)
  const [values, setValues] = useState({
    name: '',
    email: '',
    phone: '',
    subject: defaultSubject,
    message: '',
    captchaAnswer: '',
    website: '', // honeypot
  })
  const [proposedTimes, setProposedTimes] = useState<string[]>([])
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  // Set when live availability turned out to be unusable (CalDAV down / empty),
  // or when the visitor chose "suggest my own time" in `both` mode.
  const [forceRequest, setForceRequest] = useState(false)
  const [calendarDown, setCalendarDown] = useState(false)
  // Scheduling is opt-in on the contact form: a plain question needs no date.
  const [scheduleOpen, setScheduleOpen] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)

  const configuredMode = config?.booking_mode ?? 'request'
  const schedulerMode: 'request' | 'live' =
    configuredMode === 'request' || forceRequest ? 'request' : 'live'
  const chosenTimes =
    schedulerMode === 'live' ? (selectedSlot ? [selectedSlot] : []) : proposedTimes

  const loadCaptcha = () => {
    api
      .get<Captcha>('/api/captcha')
      .then(setCaptcha)
      .catch(() => setCaptcha(null))
  }

  useEffect(loadCaptcha, [])

  const set =
    (key: keyof typeof values) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setValues((current) => ({ ...current, [key]: event.target.value }))

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (submitting) return
    // An open picker with nothing picked would otherwise send a plain message
    // while the visitor believes they booked a time.
    if (scheduleOpen && chosenTimes.length === 0) {
      setResult({ ok: false, message: strings.form.pickTimeFirst })
      return
    }
    setSubmitting(true)
    setResult(null)

    const shared = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      message: values.message,
      locale,
      captcha_id: captcha?.id ?? '',
      captcha_answer: values.captchaAnswer,
      website: values.website,
      product_slug: productSlug ?? null,
    }

    try {
      let response: SubmitResult
      if (schedulerMode === 'live' && selectedSlot) {
        response = await api.post<SubmitResult>('/api/bookings', { ...shared, start: selectedSlot })
      } else {
        response = await api.post<SubmitResult>('/api/inquiries', {
          ...shared,
          subject: values.subject,
          preferred_slots: proposedTimes,
          topic,
          attachments: photos.map((photo) => photo.filename),
        })
      }
      setResult({ ok: true, message: response.message })
      setValues({
        name: '',
        email: '',
        phone: '',
        subject: defaultSubject,
        message: '',
        captchaAnswer: '',
        website: '',
      })
      setProposedTimes([])
      setSelectedSlot(null)
      setTopic('')
      photos.forEach((photo) => URL.revokeObjectURL(photo.preview))
      setPhotos([])
    } catch (error) {
      const message = error instanceof ApiError ? error.message : strings.form.error
      setResult({ ok: false, message })
      // The challenge is single-use, so a failed attempt needs a fresh one.
      loadCaptcha()
      setValues((current) => ({ ...current, captchaAnswer: '' }))
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePhotoPick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = '' // allow re-picking the same file after removal
    if (!file || photos.length >= MAX_PHOTOS || photoBusy) return
    setPhotoBusy(true)
    setPhotoError('')
    try {
      const body = new FormData()
      body.append('file', file, file.name || 'foto.jpg')
      const response = await fetch('/api/inquiries/photos', {
        method: 'POST',
        body,
        credentials: 'same-origin',
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(
          (data && typeof data.detail === 'string' && data.detail) || strings.form.photoError,
        )
      }
      setPhotos((current) => [
        ...current,
        { filename: data.filename as string, preview: URL.createObjectURL(file) },
      ])
    } catch (error) {
      setPhotoError(error instanceof Error ? error.message : strings.form.photoError)
    } finally {
      setPhotoBusy(false)
    }
  }

  function removePhoto(filename: string) {
    setPhotos((current) => {
      const removed = current.find((photo) => photo.filename === filename)
      if (removed) URL.revokeObjectURL(removed.preview)
      return current.filter((photo) => photo.filename !== filename)
    })
  }

  const schedulerHeading = useMemo(
    () => (schedulerMode === 'live' ? strings.form.pickSlot : strings.form.preferredTimes),
    [schedulerMode, strings],
  )

  if (result?.ok) {
    return (
      <div
        className="rounded-[2px] border-t-[3px] border-amber bg-paper-deep p-8 font-serif text-[24px] leading-[1.4] text-ink"
        role="status"
        data-testid="form-success"
      >
        {result.message}
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate data-testid="inquiry-form">
      {/* Honeypot: hidden from users, irresistible to naive bots. Autofill
          still reaches hidden fields sometimes, so the server only flags a
          filled one - it never drops the submission. The ignore attributes
          keep the common password managers away from it. */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <input
          id="hp_field"
          name="hp_field"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          value={values.website}
          onChange={set('website')}
        />
      </div>

      {/* --- Topic: four big chips, optional, tap again to unselect ---------- */}
      {showTopics ? (
        <fieldset className="m-0 border-0 p-0" data-testid="topic-chips">
          <legend className="mb-1 p-0 font-serif text-[22px] leading-[1.3] text-ink">
            {strings.form.topicLegend}
          </legend>
          <p className="mb-3 mt-0 font-sans text-[15px] leading-[1.5] text-slate">
            {strings.form.topicHint}
          </p>
          <div className="flex flex-wrap gap-2.5">
            {TOPIC_KEYS.map((key) => {
              const active = topic === key
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={active}
                  data-testid={`topic-${key}`}
                  onClick={() => setTopic(active ? '' : key)}
                  className={`rounded-[2px] border px-5 py-3 font-sans text-[15px] font-semibold transition-colors ${
                    active
                      ? 'border-ink bg-ink text-paper'
                      : 'border-line bg-white text-ink hover:border-ink'
                  }`}
                >
                  {strings.form.topics[key]}
                </button>
              )
            })}
          </div>
        </fieldset>
      ) : null}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="name">
            {strings.form.name} *
          </label>
          <input
            id="name"
            name="name"
            className={inputClass}
            required
            minLength={2}
            autoComplete="name"
            value={values.name}
            onChange={set('name')}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="email">
            {strings.form.email} *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            className={inputClass}
            required
            autoComplete="email"
            value={values.email}
            onChange={set('email')}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="phone">
            {strings.form.phone}
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            className={inputClass}
            autoComplete="tel"
            value={values.phone}
            onChange={set('phone')}
          />
        </div>

        {showSubject ? (
          <div>
            <label className={labelClass} htmlFor="subject">
              {strings.form.subject}
            </label>
            <input
              id="subject"
              name="subject"
              className={inputClass}
              value={values.subject}
              onChange={set('subject')}
            />
          </div>
        ) : null}
      </div>

      <div>
        <label className={labelClass} htmlFor="message">
          {strings.form.message}
        </label>
        <textarea
          id="message"
          name="message"
          rows={6}
          className={inputClass}
          value={values.message}
          onChange={set('message')}
        />
      </div>

      {/* --- Photos (optional; e.g. a damaged instrument) ------------------ */}
      {showPhotos ? (
        <div data-testid="photo-section">
          <p className="m-0 font-serif text-[22px] leading-[1.3] text-ink">
            {strings.form.photos}
          </p>
          <p className="mb-3 mt-0.5 font-sans text-[15px] leading-[1.5] text-slate">
            {strings.form.photosHint}
          </p>
          <div className="flex flex-wrap items-start gap-3">
            {photos.map((photo) => (
              <span key={photo.filename} className="relative block" data-testid="photo-thumb">
                <img
                  src={photo.preview}
                  alt=""
                  className="h-[84px] w-[84px] rounded-[2px] border border-line object-cover"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(photo.filename)}
                  aria-label={strings.form.photoRemove}
                  data-testid="photo-remove"
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-line bg-white font-sans text-[13px] leading-none text-ink shadow-sm hover:bg-paper-deep"
                >
                  ✕
                </button>
              </span>
            ))}
            {photos.length < MAX_PHOTOS ? (
              <button
                type="button"
                onClick={() => photoInput.current?.click()}
                disabled={photoBusy}
                data-testid="photo-add"
                className="flex h-[84px] w-[84px] flex-col items-center justify-center gap-1 rounded-[2px] border border-dashed border-line bg-white font-sans text-[12px] font-semibold text-ink transition-colors hover:border-ink disabled:opacity-60"
              >
                <span aria-hidden="true" className="font-serif text-[24px] leading-none">
                  +
                </span>
                {photoBusy ? '…' : strings.form.photoAdd}
              </button>
            ) : null}
          </div>
          {photoBusy ? (
            <p className="mb-0 mt-2 font-sans text-[15px] text-slate" role="status">
              {strings.form.photoUploading}
            </p>
          ) : null}
          {photoError ? (
            <p
              className="mb-0 mt-2 rounded-[2px] border border-walnut bg-paper-deep px-3 py-2 font-sans text-[15px] text-ink"
              role="alert"
              data-testid="photo-error"
            >
              {photoError}
            </p>
          ) : null}
          <input
            ref={photoInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            className="hidden"
            data-testid="photo-input"
            onChange={handlePhotoPick}
          />
        </div>
      ) : null}

      {/* --- Scheduling (optional, collapsed by default) ------------------- */}
      {config && !scheduleOpen ? (
        <button
          type="button"
          onClick={() => setScheduleOpen(true)}
          data-testid="schedule-toggle"
          className="flex items-center gap-4 rounded-[2px] border border-line bg-white px-5 py-4 text-left transition-colors hover:border-ink"
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="shrink-0 text-amber"
          >
            <path d="M4 6h16v14H4zM4 10h16M8 3.5V7M16 3.5V7" />
          </svg>
          <span>
            <span className="block font-serif text-[21px] leading-[1.3] text-ink">
              {strings.form.scheduleToggle}
            </span>
            <span className="block font-sans text-[15px] leading-[1.5] text-slate">
              {strings.form.scheduleToggleHint}
            </span>
          </span>
          <span aria-hidden="true" className="ml-auto font-serif text-[26px] text-ink">
            +
          </span>
        </button>
      ) : null}

      {config && scheduleOpen ? (
        <section className="border-t border-line pt-5" data-testid="scheduling-section">
          <p className="eyebrow m-0">{schedulerHeading}</p>
          <p className="mb-4 mt-1 font-sans text-[16px] leading-[1.5] text-slate">
            {schedulerMode === 'live'
              ? strings.form.pickSlotHint
              : fill(strings.form.preferredTimesHint, { n: config.max_proposed_slots })}
          </p>

          {calendarDown ? (
            <p
              className="mb-4 rounded-[2px] border border-line bg-paper-deep px-4 py-2.5 font-sans text-[16px] text-ink"
              data-testid="calendar-warning"
            >
              {strings.form.calendarUnavailable}
            </p>
          ) : null}

          <VisitorScheduler
            key={schedulerMode}
            config={config}
            locale={locale}
            mode={schedulerMode}
            proposed={proposedTimes}
            onProposed={setProposedTimes}
            slot={selectedSlot}
            onSlot={setSelectedSlot}
            onCalendarUnavailable={() => {
              setCalendarDown(true)
              setForceRequest(true)
            }}
          />

          {configuredMode === 'both' && !calendarDown ? (
            <button
              type="button"
              onClick={() => {
                setForceRequest((current) => !current)
                setSelectedSlot(null)
                setProposedTimes([])
              }}
              data-testid="suggest-instead"
              className="mr-6 mt-4 font-sans text-[15px] font-semibold text-ink underline underline-offset-4"
            >
              {schedulerMode === 'live' ? strings.form.suggestInstead : strings.form.showLiveInstead}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setScheduleOpen(false)
              setSelectedSlot(null)
              setProposedTimes([])
            }}
            data-testid="schedule-hide"
            className="mt-4 font-sans text-[15px] font-semibold text-slate underline underline-offset-4"
          >
            {strings.form.scheduleHide}
          </button>
        </section>
      ) : null}

      {result && !result.ok ? (
        <p
          className="rounded-[2px] border border-walnut bg-paper-deep px-4 py-3 font-sans text-[17px] text-ink"
          role="alert"
          data-testid="form-error"
        >
          {result.message}
        </p>
      ) : null}

      {/* Captcha on the left, send on the right; stacked on phones. */}
      <div className="flex flex-col gap-5 border-t border-line pt-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <label className={labelClass} htmlFor="captcha">
            {strings.form.captcha}
          </label>
          <div className="flex items-center gap-3">
            <span
              className="whitespace-nowrap font-serif text-[24px] leading-none text-ink"
              data-testid="captcha-question"
            >
              {captcha?.question ?? '…'} =
            </span>
            <input
              id="captcha"
              name="captcha"
              inputMode="numeric"
              className={`${inputClass.replace('w-full ', '')} h-[50px] w-[84px] px-0 text-center`}
              required
              autoComplete="off"
              data-testid="captcha-input"
              value={values.captchaAnswer}
              onChange={set('captchaAnswer')}
            />
          </div>
          <p className="mb-0 mt-2 font-sans text-[14px] leading-[1.5] text-slate">
            {strings.form.captchaHint}
          </p>
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="form-submit"
          className="inline-flex cursor-pointer items-center justify-center rounded-[2px] bg-ink px-8 py-[15px] font-sans text-[15px] font-semibold leading-none text-paper transition-colors duration-200 hover:bg-walnut disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting
            ? strings.form.sending
            : schedulerMode === 'live' && selectedSlot
              ? strings.form.book
              : strings.form.send}
        </button>
      </div>
    </form>
  )
}
